import { api } from './index';

export type AccessCardKind = 'VEHICLE' | 'ELEVATOR' | 'ENTRY';
export type AccessCardPickerPurpose =
  | 'STOCK'
  | 'ISSUANCE_ELEVATOR'
  | 'ISSUANCE_VEHICLE'
  | 'MONTHLY_VEHICLE';
export type AccessCardStockStatusFilter = 'AVAILABLE' | 'SOLD';
export type ParkingRecordStatus = 'OK' | 'EXPIRED' | 'LOCKED' | 'DELETED';

export interface VehicleCardType {
  id: string;
  buildingId: string;
  code: string;
  name: string;
  feeCategoryId?: string | null;
  sortOrder: number;
  isActive: boolean;
  feeCategory?: {
    id: string;
    name: string;
    feeCode?: string | null;
    quantityUnit?: string | null;
  } | null;
  _count?: {
    accessCards: number;
    monthlyVehicles: number;
  };
}

export interface AccessCard {
  id: string;
  buildingId: string;
  kind: AccessCardKind;
  cardNumber: string;
  cardCode?: string | null;
  vehicleCardTypeId?: string | null;
  notes?: string | null;
  isActive: boolean;
  isSold?: boolean;
  stockStatus?: 'SOLD' | 'AVAILABLE';
  elevatorLinked?: boolean;
  vehicleAssigned?: boolean;
  vehicleCardType?: { id: string; code: string; name: string } | null;
}

export interface AccessCardActionLog {
  id: string;
  buildingId: string;
  accessCardId?: string | null;
  kind?: AccessCardKind | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'BULK_DELETE' | 'IMPORT' | 'RESTORE';
  cardNumber?: string | null;
  cardCode?: string | null;
  notes?: string | null;
  fileName?: string | null;
  fileUrl?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  creator?: { id: string; name: string; email: string } | null;
}

export interface MonthlyVehicleActionLog {
  id: string;
  buildingId: string;
  monthlyVehicleId?: string | null;
  apartmentId?: string | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'IMPORT';
  licensePlate?: string | null;
  notes?: string | null;
  fileName?: string | null;
  fileUrl?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  creator?: { id: string; name: string; email: string } | null;
}

export interface MonthlyVehicle {
  id: string;
  buildingId: string;
  apartmentId: string;
  accessCardId?: string | null;
  vehicleCardTypeId: string;
  licensePlate?: string | null;
  vehicleModel?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  registrationDate?: string | null;
  phone?: string | null;
  ownerName?: string | null;
  parkingSpotNumber?: string | null;
  status: ParkingRecordStatus;
  notes?: string | null;
  /** All-in monthly parking fee (VND). Null = use fee category; 0 = free. */
  monthlyParkingFee?: number | string | null;
  apartment?: { id: string; code: string; name?: string | null };
  accessCard?: { id: string; cardNumber: string; cardCode?: string | null } | null;
  vehicleCardType?: { id: string; code: string; name: string };
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

async function downloadBlob(url: string, fallbackFilename: string) {
  const response = await api.get(url, { responseType: 'blob' });
  const disposition = response.headers['content-disposition'] as string | undefined;
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || fallbackFilename;
  const blobUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

export const parkingManagementApi = {
  async listVehicleCardTypes(buildingId: string): Promise<VehicleCardType[]> {
    const response = await api.get(
      `/parking-management/vehicle-card-types?buildingId=${buildingId}`,
    );
    return response.data;
  },

  async createVehicleCardType(data: {
    buildingId: string;
    name: string;
    code?: string;
    feeCategoryId?: string | null;
    sortOrder?: number;
  }): Promise<VehicleCardType> {
    const response = await api.post('/parking-management/vehicle-card-types', data);
    return response.data;
  },

  async updateVehicleCardType(
    id: string,
    data: {
      name?: string;
      code?: string;
      feeCategoryId?: string | null;
      isActive?: boolean;
      sortOrder?: number;
    },
  ): Promise<VehicleCardType> {
    const response = await api.put(`/parking-management/vehicle-card-types/${id}`, data);
    return response.data;
  },

  async removeVehicleCardType(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/parking-management/vehicle-card-types/${id}`);
    return response.data;
  },

  async listAccessCards(params: {
    buildingId: string;
    kind: AccessCardKind;
    search?: string;
    stockStatus?: AccessCardStockStatusFilter;
    vehicleCardTypeId?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: AccessCard[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      soldCount?: number;
      availableCount?: number;
      combinedElevatorVehicleCards?: boolean;
    };
  }> {
    const query = new URLSearchParams();
    query.append('buildingId', params.buildingId);
    query.append('kind', params.kind);
    if (params.search) query.append('search', params.search);
    if (params.stockStatus) query.append('stockStatus', params.stockStatus);
    if (params.vehicleCardTypeId) query.append('vehicleCardTypeId', params.vehicleCardTypeId);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const response = await api.get(`/parking-management/access-cards?${query.toString()}`);
    return response.data;
  },

  async getAccessCardActionLogs(
    buildingId: string,
    kind?: AccessCardKind,
    options?: { page?: number; limit?: number },
  ): Promise<import('./paginatedHistory').PaginatedHistoryResponse<AccessCardActionLog>> {
    const query = new URLSearchParams();
    query.append('buildingId', buildingId);
    if (kind) query.append('kind', kind);
    if (options?.page) query.append('page', String(options.page));
    if (options?.limit) query.append('limit', String(options.limit));
    const response = await api.get(
      `/parking-management/access-cards/action-logs?${query.toString()}`,
    );
    return response.data;
  },

  async lookupAccessCard(
    buildingId: string,
    cardNumber: string,
    kind: AccessCardKind = 'VEHICLE',
  ): Promise<AccessCard> {
    const query = new URLSearchParams();
    query.append('buildingId', buildingId);
    query.append('cardNumber', cardNumber);
    query.append('kind', kind);
    const response = await api.get(`/parking-management/access-cards/lookup?${query.toString()}`);
    return response.data;
  },

  async listAvailableAccessCards(params: {
    buildingId: string;
    kind: AccessCardKind;
    search?: string;
    limit?: number;
    purpose?: AccessCardPickerPurpose;
    excludeMonthlyVehicleId?: string;
  }): Promise<AccessCard[]> {
    const query = new URLSearchParams();
    query.append('buildingId', params.buildingId);
    query.append('kind', params.kind);
    if (params.search) query.append('search', params.search);
    if (params.limit) query.append('limit', String(params.limit));
    if (params.purpose) query.append('purpose', params.purpose);
    if (params.excludeMonthlyVehicleId) {
      query.append('excludeMonthlyVehicleId', params.excludeMonthlyVehicleId);
    }
    const response = await api.get(`/parking-management/access-cards/available?${query.toString()}`);
    return response.data;
  },

  async countAvailableAccessCards(
    buildingId: string,
    kind: AccessCardKind,
    purpose: AccessCardPickerPurpose = 'STOCK',
  ): Promise<{ count: number }> {
    const query = new URLSearchParams();
    query.append('buildingId', buildingId);
    query.append('kind', kind);
    query.append('purpose', purpose);
    const response = await api.get(
      `/parking-management/access-cards/available/count?${query.toString()}`,
    );
    return response.data;
  },

  async createAccessCard(data: {
    buildingId: string;
    kind: AccessCardKind;
    cardNumber: string;
    cardCode?: string;
    vehicleCardTypeId?: string;
    notes?: string;
  }): Promise<AccessCard> {
    const response = await api.post('/parking-management/access-cards', data);
    return response.data;
  },

  async updateAccessCard(
    id: string,
    data: {
      cardNumber?: string;
      cardCode?: string | null;
      vehicleCardTypeId?: string | null;
      notes?: string | null;
      isActive?: boolean;
    },
  ): Promise<AccessCard> {
    const response = await api.put(`/parking-management/access-cards/${id}`, data);
    return response.data;
  },

  async removeAccessCard(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/parking-management/access-cards/${id}`);
    return response.data;
  },

  async bulkDeleteAccessCards(data: {
    buildingId: string;
    kind: AccessCardKind;
    cardIds?: string[];
    deleteAll?: boolean;
    search?: string;
  }): Promise<{
    deletedCount: number;
    deletedCardNumbers: string[];
    skipped: Array<{ id: string; cardNumber: string; reason: string }>;
  }> {
    const response = await api.post('/parking-management/access-cards/bulk-delete', data);
    return response.data;
  },

  async restoreAccessCardsFromActionLog(
    logId: string,
    notes?: string,
  ): Promise<{
    restoredCount: number;
    restoredCardNumbers: string[];
    skipped: Array<{ cardNumber: string; reason: string }>;
  }> {
    const response = await api.post(
      `/parking-management/access-cards/action-logs/${logId}/restore`,
      { notes },
    );
    return response.data;
  },

  async listMonthlyVehicles(params: {
    buildingId: string;
    search?: string;
    page?: number;
    limit?: number;
    showLegacyDeleted?: boolean;
  }): Promise<{
    data: MonthlyVehicle[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      legacyDeletedCount: number;
    };
  }> {
    const query = new URLSearchParams();
    query.append('buildingId', params.buildingId);
    if (params.search) query.append('search', params.search);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.showLegacyDeleted) query.append('showLegacyDeleted', 'true');
    const response = await api.get(`/parking-management/monthly-vehicles?${query.toString()}`);
    return response.data;
  },

  async createMonthlyVehicle(data: {
    buildingId: string;
    apartmentId: string;
    accessCardId?: string;
    cardNumber?: string;
    vehicleCardTypeId: string;
    licensePlate?: string;
    vehicleModel?: string;
    startDate?: string;
    endDate?: string;
    phone?: string;
    ownerName?: string;
    parkingSpotNumber?: string;
    status?: ParkingRecordStatus;
    notes?: string;
    monthlyParkingFee?: number | null;
    createCardIssuanceFee?: boolean;
    createParkingFeeInvoice?: boolean;
  }): Promise<MonthlyVehicle & { createdInvoiceIds?: string[] }> {
    const response = await api.post('/parking-management/monthly-vehicles', data);
    return response.data;
  },

  async updateMonthlyVehicle(
    id: string,
    data: Partial<{
      apartmentId: string;
      accessCardId: string | null;
      cardNumber: string;
      vehicleCardTypeId: string;
      licensePlate: string;
      vehicleModel?: string | null;
      startDate: string | null;
      endDate: string | null;
      phone: string | null;
      ownerName: string | null;
      parkingSpotNumber: string | null;
      status: ParkingRecordStatus;
      notes?: string | null;
      monthlyParkingFee?: number | null;
      createCardIssuanceFee?: boolean;
    }>,
  ): Promise<MonthlyVehicle & { createdInvoiceIds?: string[] }> {
    const response = await api.put(`/parking-management/monthly-vehicles/${id}`, data);
    return response.data;
  },

  async getMonthlyVehicleActionLogs(
    buildingId: string,
    options?: { page?: number; limit?: number },
  ): Promise<import('./paginatedHistory').PaginatedHistoryResponse<MonthlyVehicleActionLog>> {
    const query = new URLSearchParams({ buildingId });
    if (options?.page) query.append('page', String(options.page));
    if (options?.limit) query.append('limit', String(options.limit));
    const response = await api.get(
      `/parking-management/monthly-vehicles/action-logs?${query.toString()}`,
    );
    return response.data;
  },

  async removeMonthlyVehicle(id: string, notes?: string): Promise<{ message: string }> {
    const response = await api.delete(`/parking-management/monthly-vehicles/${id}`, {
      data: notes?.trim() ? { notes: notes.trim() } : {},
    });
    return response.data;
  },

  async restoreMonthlyVehicle(id: string, notes?: string): Promise<MonthlyVehicle> {
    const response = await api.post(`/parking-management/monthly-vehicles/${id}/restore`, {
      ...(notes?.trim() ? { notes: notes.trim() } : {}),
    });
    return response.data;
  },

  async bulkRestoreMonthlyVehicles(
    buildingId: string,
    vehicleIds: string[],
    notes?: string,
  ): Promise<{
    restoredCount: number;
    restoredIds: string[];
    skipped: Array<{ id: string; reason: string }>;
  }> {
    const response = await api.post('/parking-management/monthly-vehicles/bulk-restore', {
      buildingId,
      vehicleIds,
      ...(notes?.trim() ? { notes: notes.trim() } : {}),
    });
    return response.data;
  },

  async bulkDeleteMonthlyVehicles(
    buildingId: string,
    vehicleIds: string[],
    notes?: string,
  ): Promise<{
    deletedCount: number;
    deletedIds: string[];
    skipped: Array<{ id: string; reason: string }>;
  }> {
    const response = await api.post('/parking-management/monthly-vehicles/bulk-delete', {
      buildingId,
      vehicleIds,
      ...(notes?.trim() ? { notes: notes.trim() } : {}),
    });
    return response.data;
  },

  async syncQuantities(buildingId: string): Promise<{ message: string }> {
    const response = await api.post('/parking-management/monthly-vehicles/sync-quantities', {
      buildingId,
    });
    return response.data;
  },

  async exportAccessCards(buildingId: string, kind: AccessCardKind): Promise<void> {
    const filename =
      kind === 'VEHICLE' ? 'the_xe.xlsx' : kind === 'ELEVATOR' ? 'the_thang_may.xlsx' : 'the_ra_vao.xlsx';
    return downloadBlob(
      `/parking-management/access-cards/export?buildingId=${buildingId}&kind=${kind}`,
      filename,
    );
  },

  async exportAccessCardSample(buildingId: string, kind: AccessCardKind): Promise<void> {
    const filename =
      kind === 'VEHICLE'
        ? 'mau_the_xe.xlsx'
        : kind === 'ELEVATOR'
          ? 'mau_the_thang_may.xlsx'
          : 'mau_the_ra_vao.xlsx';
    return downloadBlob(
      `/parking-management/access-cards/export-sample?buildingId=${buildingId}&kind=${kind}`,
      filename,
    );
  },

  async importAccessCards(formData: FormData): Promise<ImportResult> {
    const response = await api.post('/parking-management/access-cards/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async exportMonthlyVehicles(buildingId: string): Promise<void> {
    return downloadBlob(
      `/parking-management/monthly-vehicles/export?buildingId=${buildingId}`,
      'xe_thang.xlsx',
    );
  },

  async exportMonthlyVehicleSample(buildingId: string): Promise<void> {
    return downloadBlob(
      `/parking-management/monthly-vehicles/export-sample?buildingId=${buildingId}`,
      'mau_xe_thang.xlsx',
    );
  },

  async importMonthlyVehicles(formData: FormData): Promise<ImportResult> {
    const response = await api.post('/parking-management/monthly-vehicles/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
